import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InfoToast } from '@services/utils/ToastUtils';
import { useDispatch } from 'react-redux';
import { EUIDealsheetService } from '../../../services/essential-ui/eui-dealsheet/EUIDealsheetService';
import { DealSheet } from '../../../types/essential-ui/DealSheet';
import { setDealsheetMvo } from '../mvoUI/MvoUiSlice';

export type EUIDealSheetState = { dealSheet: DealSheet | null; loading: boolean; error: boolean };
const initialEUIDealSheetState: EUIDealSheetState = {
  dealSheet: null,
  loading: false,
  error: false,
};

export const getEUIDealsheetMvo = createAsyncThunk(
  'dealSheet/getEUIDealsheetMvo',
  async (params: {
    id: string;
    dealerId: string;
    userName: string;
    rank: string;
    type?: string;
  }) => {
    try {
      const response = await EUIDealsheetService.getEUIDealsheetMvo(
        params.id,
        params.dealerId,
        params.userName,
        params.rank,
      );
      if (response) {
        return response;
      }
    } catch (err) {
      if (params.type == 'toggle' && params.rank == '2') {
        InfoToast(
          'Unable to load second Replacement Vehicle. Attempting to load first Replacement Vehicle',
          5000,
        );
        try {
          const response = await EUIDealsheetService.getEUIDealsheetMvo(
            params.id,
            params.dealerId,
            params.userName,
            '1',
          );
          if (response) {
            return response;
          }
          const dispatch = useDispatch();
          dispatch(setDealsheetMvo(1));
        } catch (err) {
          throw new Error('Failed to load dealsheet');
        }
      } else {
        throw new Error('Failed to load dealsheet');
      }
    }
  },
);

export const dealSheetSlice = createSlice({
  name: 'dealSheet',
  initialState: initialEUIDealSheetState,
  reducers: {
    dealSheetLoading(state) {
      state.dealSheet = null;
      state.loading = true;
      state.error = false;
    },
    dealSheetLoaded(state, action: PayloadAction<DealSheet>) {
      state.dealSheet = action.payload;
      state.loading = false;
      state.error = false;
    },
    dealSheetSubscriptionUpdated(
      state,
      action: PayloadAction<{ communicationPreference: string }>,
    ) {
      if (state.dealSheet) {
        state.dealSheet.customers[0].communicationPreference =
          action.payload.communicationPreference;
      }
      state.loading = false;
      state.error = false;
    },
    dealSheetError(state) {
      state.dealSheet = null;
      state.loading = false;
      state.error = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEUIDealsheetMvo.pending, (state) => {
        state.dealSheet = null;
        state.loading = true;
        state.error = false;
      })
      .addCase(getEUIDealsheetMvo.fulfilled, (state, action) => {
        if (action.payload) {
          state.dealSheet = action.payload;
        }
        state.loading = false;
        state.error = false;
      })
      .addCase(getEUIDealsheetMvo.rejected, (state) => {
        state.dealSheet = null;
        state.loading = false;
        state.error = true;
      });
  },
});

export const { dealSheetLoading, dealSheetLoaded, dealSheetSubscriptionUpdated, dealSheetError } =
  dealSheetSlice.actions;

export default dealSheetSlice.reducer;
